import axios from "axios";
import Keycloak from "keycloak-js/lib/keycloak.js";

const DJANGO_BASE_URL = process.env.REACT_APP_DJANGO_BASE_URL || "http://localhost:8000";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || `${DJANGO_BASE_URL}/api`;
const KEYCLOAK_URL = process.env.REACT_APP_KEYCLOAK_URL || "http://localhost:8080";
const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM || "ngrisetech";
const KEYCLOAK_CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || "ngrisetech-apbot-ui";
const KEYCLOAK_ADMIN_BASE_URL =
  process.env.REACT_APP_KEYCLOAK_ADMIN_BASE_URL || `${KEYCLOAK_URL}/admin/realms/${KEYCLOAK_REALM}`;

export const MANAGED_KEYCLOAK_ROLES = [
  "SuperUser",
  "Processor",
  "Checker",
  "Uploader",
  "Data_Uploader",
  "Initiator",
  "Approver",
];

const keycloak = new Keycloak({
  url: KEYCLOAK_URL,
  realm: KEYCLOAK_REALM,
  clientId: KEYCLOAK_CLIENT_ID,
});

let authInitialized = false;
let authInitPromise = null;

function resolvePkceMethod() {
  const configuredMethod = (process.env.REACT_APP_KEYCLOAK_PKCE_METHOD || "").trim().toLowerCase();
  if (configuredMethod === "false" || configuredMethod === "off" || configuredMethod === "none") {
    return false;
  }
  if (configuredMethod === "s256") {
    return "S256";
  }
  return window.isSecureContext ? "S256" : false;
}

async function getAccessToken() {
  if (!keycloak.authenticated) {
    return null;
  }
  try {
    await keycloak.updateToken(30);
  } catch (error) {
    await keycloak.login({ redirectUri: getKeycloakRedirectUri() });
    return null;
  }
  return keycloak.token || null;
}

function getKeycloakRedirectUri() {
  return `${window.location.origin}${window.location.pathname}`;
}

export function getLegacyUrl(path) {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  return `${DJANGO_BASE_URL}${path}`;
}

export async function ensureCsrfToken() {
  return null;
}

export async function initializeAuth() {
  if (authInitialized) {
    return keycloak.authenticated === true;
  }
  if (authInitPromise) {
    return authInitPromise;
  }

  authInitPromise = keycloak
    .init({
      onLoad: "check-sso",
      checkLoginIframe: false,
      pkceMethod: resolvePkceMethod(),
      responseMode: "query",
    })
    .then((authenticated) => {
      authInitialized = true;
      return authenticated;
    })
    .catch(() => {
      authInitialized = true;
      return false;
    });

  return authInitPromise;
}

export function loginWithKeycloak() {
  return keycloak.login({
    redirectUri: getKeycloakRedirectUri(),
  });
}

export function logoutFromKeycloak() {
  return keycloak.logout({
    redirectUri: window.location.origin,
  });
}

export function getKeycloakProfile() {
  if (!keycloak.tokenParsed) {
    return null;
  }
  return {
    sub: keycloak.tokenParsed.sub,
    username: keycloak.tokenParsed.preferred_username,
    email: keycloak.tokenParsed.email,
    name: keycloak.tokenParsed.name,
  };
}

export function getKeycloakSession() {
  if (!keycloak.authenticated || !keycloak.tokenParsed) {
    return {
      authenticated: false,
      roles: [],
      teams: [],
      profile: null,
    };
  }

  const realmAccess = keycloak.tokenParsed.realm_access || {};
  const roles = Array.isArray(realmAccess.roles) ? realmAccess.roles : [];
  const rawGroups = Array.isArray(keycloak.tokenParsed.groups) ? keycloak.tokenParsed.groups : [];
  const groupTeams = rawGroups
    .map((groupPath) => String(groupPath || ""))
    .map((groupPath) => groupPath.split("/").filter(Boolean).pop() || "")
    .filter(Boolean);
  const tokenTeams = Array.isArray(keycloak.tokenParsed.teams) ? keycloak.tokenParsed.teams : [];
  const teams = groupTeams.length ? groupTeams : tokenTeams;
  const organizations = Array.isArray(keycloak.tokenParsed.organizations) ? keycloak.tokenParsed.organizations : [];

  return {
    authenticated: true,
    roles,
    teams,
    organizations,
    profile: getKeycloakProfile()
  };
}

const djangoClient = axios.create({
  baseURL: DJANGO_BASE_URL,
  timeout: 60000,
});

const keycloakAdminClient = axios.create({
  baseURL: KEYCLOAK_ADMIN_BASE_URL,
  timeout: 60000,
});

djangoClient.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  const headers = config.headers || {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  config.headers = headers;
  return config;
});

keycloakAdminClient.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("Keycloak authentication is required.");
  }
  const headers = config.headers || {};
  headers.Authorization = `Bearer ${token}`;
  config.headers = headers;
  return config;
});

function toAbsoluteBackendUrl(url) {
  if (!url) {
    return `${DJANGO_BASE_URL}/`;
  }
  if (typeof url !== "string") {
    return String(url);
  }
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `${DJANGO_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

function headersToObject(headers) {
  if (!headers) {
    return {};
  }
  if (headers instanceof Headers) {
    const result = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
  return { ...headers };
}

export function installFetchBridge() {
  const originalFetch = window.fetch.bind(window);

  const resolveWithCredentials = (credentials, requestUrl) => {
    const effectiveCredentials = credentials || "same-origin";

    if (effectiveCredentials === "include") {
      return true;
    }
    if (effectiveCredentials === "omit") {
      return false;
    }

    try {
      const absolute = new URL(requestUrl, window.location.href);
      return absolute.origin === window.location.origin;
    } catch (error) {
      return false;
    }
  };

  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : input.url;
    const method = (init.method || "GET").toUpperCase();

    // Preserve non-HTTP calls and data URIs.
    if (typeof url === "string" && (url.startsWith("data:") || url.startsWith("blob:"))) {
      return originalFetch(input, init);
    }

    const requestHeaders = headersToObject(init.headers);
    const targetUrl = toAbsoluteBackendUrl(url);
    const requestCredentials =
      init.credentials || (typeof input === "string" ? undefined : input.credentials);
    const response = await djangoClient.request({
      url: targetUrl,
      method,
      data: init.body,
      headers: requestHeaders,
      withCredentials: resolveWithCredentials(requestCredentials, targetUrl),
      responseType: "arraybuffer",
      validateStatus: () => true,
    });

    const responseHeaders = new Headers();
    Object.entries(response.headers || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        responseHeaders.set(key, String(value));
      }
    });

    return new Response(response.data, {
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
    });
  };
}

export async function apiRequest(path, options = {}) {
  const method = (options.method || "GET").toUpperCase();
  const headers = headersToObject(options.headers);
  const url = `${API_BASE_URL}${path}`;
  const hasBody = options.body !== undefined && options.body !== null;
  let body = options.body;

  if (hasBody && !(body instanceof FormData) && typeof body === "object") {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
    body = JSON.stringify(body);
  }

  const response = await djangoClient.request({
    url,
    method,
    data: body,
    headers,
    validateStatus: () => true,
  });

  if (response.status < 200 || response.status >= 300) {
    const payload = response.data || {};
    const message = payload.message || payload.error || "Request failed.";
    const error = new Error(message);
    error.payload = payload;
    error.status = response.status;
    throw error;
  }

  return response.data;
}

export async function fetchUserRightsContext() {
  const payload = await apiRequest("/user-rights/context/");
  return payload.data || { teams: [], users: [], supportedRoles: [] };
}

export async function fetchUserRights(team, role) {
  const payload = await apiRequest(
    `/user-rights/?team=${encodeURIComponent(team)}&role=${encodeURIComponent(role)}`,
  );
  return payload;
}

export async function saveUserRights({ team, role, assignments }) {
  return apiRequest("/user-rights/", {
    method: "POST",
    body: {
      team,
      role,
      assignments,
    },
  });
}

export async function fetchProcessorMatrix(team = "") {
  const query = team ? `?team=${encodeURIComponent(team)}` : "";
  const payload = await apiRequest(`/user-rights/processor-matrix/${query}`);
  return payload.data || [];
}

export async function saveProcessorMatrix(matrix, team = "") {
  return apiRequest("/user-rights/processor-matrix/save/", {
    method: "POST",
    body: {
      matrix,
      team,
    },
  });
}

export async function fetchInvoiceApprovalMatrix(matrixType) {
  const payload = await apiRequest(
    `/invoice-approval-matrix/?matrix_type=${encodeURIComponent(matrixType)}`,
  );
  return payload.data || [];
}

export async function saveInvoiceApprovalMatrixRow(row) {
  return apiRequest("/invoice-approval-matrix/save/", {
    method: "POST",
    body: row,
  });
}

export async function importInvoiceApprovalMatrix(matrixType, file) {
  const formData = new FormData();
  formData.append("matrix_type", matrixType);
  formData.append("file", file);
  return apiRequest("/invoice-approval-matrix/import/", {
    method: "POST",
    body: formData,
  });
}

export async function deleteInvoiceApprovalMatrixRow(rowId) {
  return apiRequest(`/invoice-approval-matrix/${rowId}/delete/`, {
    method: "POST",
  });
}


export async function fetchInvoicePOConditions() {
    const payload = await apiRequest("/invoice-po-conditions/");

    return payload.data || {
        conditions: [],
        headers: { PO: [], GRN: [] },
        budget: false,
    };
}

export async function fetchInvoiceNonPOConditions() {
    const payload = await apiRequest("/invoice-non-po-conditions/");

    return payload.data || {
        conditions: [],
        headers: { "Vendor Master": [], "Invoice OCR": [] }
    };
}

export async function saveInvoiceNonPOCondition(condition) {
  return apiRequest("/invoice-non-po-conditions/save/", {
    method: "POST",
    body: condition,
  });
}

export async function deleteInvoiceNonPOCondition(conditionId) {
  return apiRequest(`/invoice-non-po-conditions/${conditionId}/delete/`, {
    method: "POST",
  });
}

export async function saveInvoicePOCondition(condition) {
  return apiRequest("/invoice-po-conditions/save/", {
    method: "POST",
    body: condition,
  });
}

export async function deleteInvoicePOCondition(conditionId) {
  return apiRequest(`/invoice-po-conditions/${conditionId}/delete/`, {
    method: "POST",
  });
}

export async function saveInvoicePOCustomMapping(mapping) {
  return apiRequest("/invoice-po-custom-mappings/save/", {
    method: "POST",
    body: mapping,
  });
}

export async function deleteInvoicePOCustomMapping(mappingId) {
  return apiRequest(`/invoice-po-custom-mappings/${mappingId}/delete/`, {
    method: "POST",
  });
}

export async function fetchInvoiceInitiatorQueue(filters = {}) {
  const params = new URLSearchParams();
  if (filters.queueType) {
    params.set("queue_type", filters.queueType);
  }
  if (filters.status) {
    params.set("status", filters.status);
  }
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const payload = await apiRequest(`/invoice-initiator-queue/${suffix}`);
  return payload.data || [];
}

export async function fetchInvoiceInitiatorQueueDetail(queueId) {
  const payload = await apiRequest(`/invoice-initiator-queue/${queueId}/`);
  return payload.data || null;
}

export async function recalculateInvoiceInitiatorQueue(queueId, values) {
  const payload = await apiRequest(`/invoice-initiator-queue/${queueId}/recalculate/`, {
    method: "POST",
    body: values,
  });
  return payload.data || null;
}

export async function submitInvoiceInitiatorToWorkflow(queueId) {
  return apiRequest(`/invoice-initiator-queue/${queueId}/submit-workflow/`, {
    method: "POST",
  });
}

export async function fetchInvoiceApprovalWorkflows(filters = {}) {
  const params = new URLSearchParams();

  if (filters.status) {
    params.set("status", filters.status);
  }

  const suffix = params.toString() ? `?${params.toString()}` : "";

  const payload = await apiRequest(`/invoice-approval-workflows/${suffix}`);
  return payload.data || [];
}


function keycloakRequestError(error, fallbackMessage) {
  const payload = error?.response?.data;
  if (typeof payload === "string" && payload.trim()) {
    return new Error(payload);
  }
  if (payload?.errorMessage) {
    return new Error(payload.errorMessage);
  }
  if (payload?.error) {
    return new Error(payload.error);
  }
  return new Error(fallbackMessage);
}

function groupAttr(group, key, fallback = "") {
  const attrs = group.attributes || {};
  const value = attrs[key];
  if (Array.isArray(value) && value.length) {
    return String(value[0]);
  }
  if (typeof value === "string") {
    return value;
  }
  return fallback;
}

export async function fetchKeycloakTeams() {
  try {
    const response = await keycloakAdminClient.get("/groups");
    const groups = response.data || [];
    return groups.map((group) => ({
      id: group.id,
      team: group.name,
      team_name: groupAttr(group, "team_name", group.name || ""),
      department: groupAttr(group, "department", ""),
      location: groupAttr(group, "location", ""),
      path: group.path || `/${group.name}`,
    }));
  } catch (error) {
    throw keycloakRequestError(error, "Unable to load teams from Keycloak.");
  }
}

export async function createKeycloakTeam({
  team,
  team_name = "",
  department = "",
  location = "",
}) {
  const name = (team || "").trim();
  if (!name) {
    throw new Error("Team code is required.");
  }
  try {
    await keycloakAdminClient.post("/groups", {
      name,
      attributes: {
        team_name: [team_name.trim() || name],
        department: [department.trim()],
        location: [location.trim()],
      },
    });
  } catch (error) {
    throw keycloakRequestError(error, "Unable to create team in Keycloak.");
  }
}

export async function updateKeycloakTeam(
  groupId,
  {
    team,
    team_name = "",
    department = "",
    location = "",
  },
) {
  if (!groupId) {
    throw new Error("Team id is required.");
  }
  const name = (team || "").trim();
  if (!name) {
    throw new Error("Team code is required.");
  }

  try {
    await keycloakAdminClient.put(`/groups/${groupId}`, {
      id: groupId,
      name,
      attributes: {
        team_name: [team_name.trim() || name],
        department: [department.trim()],
        location: [location.trim()],
      },
    });
  } catch (error) {
    throw keycloakRequestError(error, "Unable to update team in Keycloak.");
  }
}

export async function deleteKeycloakTeam(groupId) {
  if (!groupId) {
    throw new Error("Team id is required.");
  }
  try {
    await keycloakAdminClient.delete(`/groups/${groupId}`);
  } catch (error) {
    throw keycloakRequestError(error, "Unable to delete team from Keycloak.");
  }
}

async function fetchKeycloakUserRoles(userId) {
  const response = await keycloakAdminClient.get(`/users/${userId}/role-mappings/realm`);
  return response.data || [];
}

async function fetchKeycloakUserGroups(userId) {
  const response = await keycloakAdminClient.get(`/users/${userId}/groups`);
  return response.data || [];
}

export async function fetchKeycloakUsersForManagement() {
  try {
    const usersResp = await keycloakAdminClient.get("/users", { params: { max: 1000 } });
    const users = usersResp.data || [];

    const enriched = await Promise.all(
      users.map(async (user) => {
        const [roles, groups] = await Promise.all([
          fetchKeycloakUserRoles(user.id),
          fetchKeycloakUserGroups(user.id),
        ]);

        return {
          id: user.id,
          first_name: user.firstName || "",
          email: user.email || user.username || "",
          role: roles.map((role) => role.name).filter(Boolean),
          status: user.enabled ? "Active" : "Disabled",
          team: groups.map((group) => group.name).filter(Boolean),
        };
      }),
    );

    return enriched.filter((user) => user.email);
  } catch (error) {
    throw keycloakRequestError(error, "Unable to load users from Keycloak.");
  }
}

async function fetchRealmRolesMap() {
  const response = await keycloakAdminClient.get("/roles");
  const roles = response.data || [];
  const map = {};
  roles.forEach((role) => {
    if (role?.name) {
      map[role.name] = role;
    }
  });
  return map;
}

function isManagedRealmRole(roleName) {
  if (!roleName) {
    return false;
  }
  if (MANAGED_KEYCLOAK_ROLES.includes(roleName)) {
    return true;
  }
  if (roleName.startsWith("default-roles-")) {
    return false;
  }
  const excluded = new Set([
    "offline_access",
    "uma_authorization",
    "default-roles-master",
  ]);
  return !excluded.has(roleName);
}

export async function fetchKeycloakRealmRoles() {
  try {
    const response = await keycloakAdminClient.get("/roles");
    const roles = response.data || [];
    return roles
      .filter((role) => isManagedRealmRole(role?.name))
      .map((role) => ({
        id: role.id || role.name,
        name: role.name || "",
        description: role.description || "",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    throw keycloakRequestError(error, "Unable to load roles from Keycloak.");
  }
}

export async function createKeycloakRealmRole({ name, description = "" }) {
  const roleName = (name || "").trim();
  if (!roleName) {
    throw new Error("Role name is required.");
  }
  try {
    await keycloakAdminClient.post("/roles", {
      name: roleName,
      description: description.trim(),
    });
  } catch (error) {
    throw keycloakRequestError(error, "Unable to create role in Keycloak.");
  }
}

export async function updateKeycloakRealmRole(name, { description = "" }) {
  const roleName = (name || "").trim();
  if (!roleName) {
    throw new Error("Role name is required.");
  }
  try {
    const roleResponse = await keycloakAdminClient.get(`/roles/${encodeURIComponent(roleName)}`);
    const role = roleResponse.data || {};
    await keycloakAdminClient.put(`/roles/${encodeURIComponent(roleName)}`, {
      ...role,
      name: roleName,
      description: description.trim(),
    });
  } catch (error) {
    throw keycloakRequestError(error, "Unable to update role in Keycloak.");
  }
}

export async function deleteKeycloakRealmRole(name) {
  const roleName = (name || "").trim();
  if (!roleName) {
    throw new Error("Role name is required.");
  }
  try {
    await keycloakAdminClient.delete(`/roles/${encodeURIComponent(roleName)}`);
  } catch (error) {
    throw keycloakRequestError(error, "Unable to delete role from Keycloak.");
  }
}

async function syncUserRoles(userId, selectedRoleNames) {
  const [currentRoles, roleMap] = await Promise.all([
    fetchKeycloakUserRoles(userId),
    fetchRealmRolesMap(),
  ]);

  const currentManagedRoles = currentRoles.filter((role) => MANAGED_KEYCLOAK_ROLES.includes(role.name));
  if (currentManagedRoles.length) {
    await keycloakAdminClient.delete(`/users/${userId}/role-mappings/realm`, {
      data: currentManagedRoles,
    });
  }

  const toAdd = selectedRoleNames
    .map((name) => roleMap[name])
    .filter(Boolean);
  if (toAdd.length) {
    await keycloakAdminClient.post(`/users/${userId}/role-mappings/realm`, toAdd);
  }
}

async function syncUserGroups(userId, teamNames) {
  const [currentGroups, allGroups] = await Promise.all([
    fetchKeycloakUserGroups(userId),
    fetchKeycloakTeams(),
  ]);

  const groupIdByName = {};
  allGroups.forEach((group) => {
    if (group.team && group.id) {
      groupIdByName[group.team] = group.id;
    }
  });

  const targetIds = new Set((teamNames || []).map((team) => groupIdByName[team]).filter(Boolean));
  const currentIds = new Set(currentGroups.map((group) => group.id).filter(Boolean));

  const toJoin = [...targetIds].filter((id) => !currentIds.has(id));
  const toLeave = [...currentIds].filter((id) => !targetIds.has(id));

  await Promise.all([
    ...toJoin.map((groupId) => keycloakAdminClient.put(`/users/${userId}/groups/${groupId}`)),
    ...toLeave.map((groupId) => keycloakAdminClient.delete(`/users/${userId}/groups/${groupId}`)),
  ]);
}

async function lookupUserByEmail(email) {
  const response = await keycloakAdminClient.get("/users", {
    params: { email, exact: true },
  });
  const users = response.data || [];
  return users[0] || null;
}

async function setUserPassword(userId, password) {
  await keycloakAdminClient.put(`/users/${userId}/reset-password`, {
    type: "password",
    value: password,
    temporary: false,
  });
}

export async function saveKeycloakUser({
  id = "",
  name,
  email,
  password,
  role,
  team,
  status,
}) {
  const normalizedEmail = (email || "").trim().toLowerCase();
  const selectedRoles = Array.isArray(role) ? role : [];
  const selectedTeams = Array.isArray(team) ? team : [];
  const enabled = status === "Active";

  if (!name?.trim() || !normalizedEmail || !selectedRoles.length) {
    throw new Error("Name, email and at least one role are required.");
  }

  try {
    let userId = id || "";

    if (!userId) {
      if (!password) {
        throw new Error("Password is required for new user.");
      }

      await keycloakAdminClient.post("/users", {
        username: normalizedEmail,
        email: normalizedEmail,
        firstName: name.trim(),
        enabled,
        emailVerified: true,
        credentials: [
          {
            type: "password",
            value: password,
            temporary: false,
          },
        ],
      });

      const created = await lookupUserByEmail(normalizedEmail);
      if (!created?.id) {
        throw new Error("User was created but could not be retrieved.");
      }
      userId = created.id;
    } else {
      await keycloakAdminClient.put(`/users/${userId}`, {
        email: normalizedEmail,
        username: normalizedEmail,
        firstName: name.trim(),
        enabled,
      });
      if (password) {
        await setUserPassword(userId, password);
      }
    }

    await Promise.all([
      syncUserRoles(userId, selectedRoles),
      syncUserGroups(userId, selectedTeams),
    ]);
  } catch (error) {
    throw keycloakRequestError(error, "Unable to save user in Keycloak.");
  }
}

export async function deleteKeycloakUser(userId) {
  if (!userId) {
    throw new Error("User id is required.");
  }
  try {
    await keycloakAdminClient.delete(`/users/${userId}`);
  } catch (error) {
    throw keycloakRequestError(error, "Unable to delete user from Keycloak.");
  }
}

export async function diagnoseKeycloakAdminAccess() {
  try {
    await keycloakAdminClient.get("/users", { params: { max: 1 } });
    return { ok: true, code: "ok", message: "" };
  } catch (error) {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      return {
        ok: false,
        code: "permission",
        message:
          "Missing Keycloak admin permission. Assign required realm-management roles to this user.",
      };
    }
    if (error?.code === "ERR_NETWORK" || !error?.response) {
      return {
        ok: false,
        code: "cors",
        message:
          "Unable to reach Keycloak Admin API from browser. Check Keycloak admin endpoint CORS/network access for this origin.",
      };
    }
    return {
      ok: false,
      code: "unknown",
      message: "Unable to access Keycloak Admin API.",
    };
  }
}
